import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  useGetNotificationsQuery,
  useMarkAllReadMutation,
  useMarkReadMutation,
  type GetNotificationsResponse,
  type NotificationSummary,
} from '@proaxiv/server-api'
import { formatNotification } from '../utils/formatNotification'
import { useAnchoredMenuPosition } from '../hooks/useAnchoredMenuPosition'
import { Button } from './Button'

const BellIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
)

/** Formats an ISO timestamp as a short relative time string (e.g. "5m ago", "yesterday"). */
function timeAgo(iso: string, locale: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffSeconds = Math.round((then - Date.now()) / 1000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const divisions: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ]
  let duration = diffSeconds
  for (const [amount, unit] of divisions) {
    if (Math.abs(duration) < amount) return rtf.format(Math.round(duration), unit)
    duration /= amount
  }
  return ''
}

/**
 * Top-bar notification bell: shows an unread-count badge and a dropdown listing only the
 * *unread* notifications (org lifecycle, event admin activity, etc.) — see #156. Clicking a
 * notification marks it read and navigates to its `link` (if any); a footer link opens the full
 * `/notifications` page, which lists every notification, read and unread, with bulk actions.
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // Renders the dropdown via a portal anchored with `position: fixed`, computed from the bell's
  // real on-screen coordinates (see #228). The header is `transform-gpu` (for its slide
  // transition) and the collapsed sidebar rail sits at the same z-index right next to it — a
  // plain `absolute` panel nested inside the header stays trapped in the header's own stacking
  // context, so however high its own `z-index` claims to be, it still paints *underneath* the
  // sidebar rail on mobile, visually clipping its left edge. Portaling to `document.body` (the
  // same pattern `OrgSwitcher` uses) escapes that trapped stacking context entirely.
  const menuPosition = useAnchoredMenuPosition(containerRef, open, 'right', 420)

  const { data: rawData, isFetching, refetch } = useGetNotificationsQuery({ page: 0, size: 20 })
  const [markAllRead] = useMarkAllReadMutation()
  const [markRead] = useMarkReadMutation()
  const data = rawData as GetNotificationsResponse | undefined

  const unreadCount = data?.unreadCount ?? 0
  const unreadNotifications = (data?.notifications ?? []).filter((n) => !n.read)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node
      // The menu is rendered via a portal into `document.body`, so it lives outside
      // `containerRef` and must be checked separately.
      if (containerRef.current?.contains(target)) return
      if (dropdownRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  async function handleMarkAllRead() {
    try {
      await markAllRead().unwrap()
      refetch()
    } catch {
      // Best-effort — the badge simply won't clear if this fails.
    }
  }

  function handleToggle() {
    setOpen((o) => !o)
  }

  async function handleItemClick(notification: NotificationSummary) {
    setOpen(false)
    if (!notification.read) {
      try {
        await markRead({ notificationId: notification.notificationId }).unwrap()
      } catch {
        // Best-effort — navigation still proceeds even if the read-flag update fails.
      }
    }
    if (notification.link) navigate(notification.link)
  }

  function handleSeeAll() {
    setOpen(false)
    navigate('/notifications')
  }

  return (
    <div className="relative" ref={containerRef}>
      <Button
        type="button"
        variant="unstyled"
        aria-label={t('header_notifications')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleToggle}
        className="text-pax-gray hover:bg-pax-light-purple relative flex h-9 w-9 items-center justify-center rounded-xl transition-colors focus-visible:outline-none"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="bg-pax-red absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold text-white"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open &&
        menuPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              transform: 'translateX(-100%)',
            }}
            // `max-w-[calc(100vw-2rem)]` keeps at least a 1rem margin on both sides on very
            // narrow phones, in addition to the portal fix above — belt and suspenders.
            className="border-pax-purple/30 z-50 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border bg-white shadow-lg"
          >
            <div className="border-pax-purple/20 flex items-center justify-between border-b px-4 py-2.5">
              <span className="text-pax-black text-sm font-semibold">
                {t('header_notifications')}
              </span>
              <Button
                type="button"
                variant="unstyled"
                onClick={() => void handleMarkAllRead()}
                disabled={unreadCount === 0}
                className="text-pax-blue text-xs font-medium hover:underline disabled:opacity-40"
              >
                {t('notif_mark_all_read')}
              </Button>
            </div>

            <ul className="max-h-96 overflow-y-auto">
              {isFetching && unreadNotifications.length === 0 && (
                <li className="text-pax-gray px-4 py-6 text-center text-sm">
                  {t('notif_loading')}
                </li>
              )}
              {!isFetching && unreadNotifications.length === 0 && (
                <li className="text-pax-gray px-4 py-6 text-center text-sm">{t('notif_empty')}</li>
              )}
              {unreadNotifications.map((notification) => {
                const copy = formatNotification(notification, t)
                return (
                  <li key={notification.notificationId} role="none">
                    <Button
                      type="button"
                      variant="unstyled"
                      role="menuitem"
                      onClick={() => void handleItemClick(notification)}
                      className="border-pax-purple/10 hover:bg-pax-light-purple bg-pax-light-purple/40 flex w-full flex-col items-start gap-0.5 border-b px-4 py-3 text-left transition-colors last:border-b-0"
                    >
                      <span className="text-pax-black text-sm font-semibold">{copy.title}</span>
                      <span className="text-pax-gray line-clamp-2 text-xs">{copy.message}</span>
                      <span className="text-pax-gray text-xs">
                        {timeAgo(notification.createdAt, i18n.language)}
                      </span>
                    </Button>
                  </li>
                )
              })}
            </ul>

            <div className="border-pax-purple/20 border-t">
              <Button
                type="button"
                variant="unstyled"
                onClick={handleSeeAll}
                className="text-pax-blue hover:bg-pax-light-purple w-full px-4 py-2.5 text-center text-sm font-medium transition-colors"
              >
                {t('notif_see_all')}
              </Button>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
